import { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Target, Building2, Mail, Calendar, 
  User, AlertCircle, Loader2, FileText, TrendingUp, Clock, Download, Paperclip
} from 'lucide-react';
import AuthContext from '../../../../../context/AuthContext';
import superAdminAuditService from '../../../../../services/audit_services/superAdminAuditService';
import superAdminAttachmentService from '../../../../../services/audit_services/superAdminAttachmentService';

export default function AllRecommendationActionsDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionData, setActionData] = useState(null);
  const [downloadingAttachment, setDownloadingAttachment] = useState(null);
  const [downloadMessage, setDownloadMessage] = useState(null);

  useEffect(() => {
    if (id) {
      fetchActionDetails();
    }
  }, [id]);

  useEffect(() => {
    if (downloadMessage) {
      const timer = setTimeout(() => {
        setDownloadMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [downloadMessage]);

  const fetchActionDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await superAdminAuditService.getRecommendationActionDetails(authTokens.access, id);
      setActionData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    setDownloadingAttachment(attachmentId);
    setDownloadMessage(null);
    try {
      const result = await superAdminAttachmentService.downloadFollowupAttachment(authTokens.access, attachmentId);
      if (result.success) {
        setDownloadMessage({ type: 'success', text: 'Fichier téléchargé avec succès' });
      } else {
        setDownloadMessage({ type: 'error', text: result.error || 'Erreur lors du téléchargement' });
      }
    } catch (err) {
      setDownloadMessage({ type: 'error', text: 'Erreur lors du téléchargement du fichier' });
    } finally {
      setDownloadingAttachment(null);
    }
  };

  if (loading) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600">Chargement des détails...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="ml-2 text-sm text-red-700">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!actionData) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="text-center py-12">
          <p className="text-gray-500">Aucune donnée trouvée</p>
        </div>
      </div>
    );
  }

  const { action, organization, recommendation_owner, created_by, last_updated_by, followups, statistics } = actionData;

  return (
    <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
      {/* Download Message */}
      {downloadMessage && (
        <div className={`mb-4 p-4 rounded-lg border ${
          downloadMessage.type === 'success' 
            ? 'bg-green-50 border-green-200 text-green-700' 
            : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          <div className="flex items-center">
            {downloadMessage.type === 'success' ? (
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <AlertCircle className="h-5 w-5 mr-2" />
            )}
            <span className="text-sm">{downloadMessage.text}</span>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/admin-panel/recommendation-actions')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Action de recommandation</h1>
            <p className="text-xs text-gray-500">Détails de l'action</p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Action Information */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <Target className="h-5 w-5 text-gray-600 mr-2" />
            <h2 className="text-sm font-semibold text-gray-900">Informations de l'action</h2>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                {action?.recommendation_text && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Texte de la recommandation</label>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{action.recommendation_text}</p>
                  </div>
                )}
                {action?.status && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Statut</label>
                    <div>
                      <span className={`inline-block px-2 py-1 text-xs rounded ${
                        action.status === 'achieved' ? 'bg-green-100 text-green-700' :
                        action.status === 'completed' ? 'bg-purple-100 text-purple-700' :
                        action.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                        action.status === 'started' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {action.status}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="space-y-3">
                {action?.created_at && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Créé le</label>
                    <div className="flex items-center">
                      <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                      <p className="text-sm text-gray-900">
                        {new Date(action.created_at).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                )}
                {action?.updated_at && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Mis à jour le</label>
                    <div className="flex items-center">
                      <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                      <p className="text-sm text-gray-900">
                        {new Date(action.updated_at).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {action?.elements_of_recommendation && (
              <div>
                <label className="text-xs font-medium text-gray-500">Éléments de la recommandation</label>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{action.elements_of_recommendation}</p>
              </div>
            )}

            {action?.kpi_of_recommendation && (
              <div>
                <label className="text-xs font-medium text-gray-500">KPI de la recommandation</label>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{action.kpi_of_recommendation}</p>
              </div>
            )}

            {action?.delay_of_recommendation && (
              <div>
                <label className="text-xs font-medium text-gray-500">Délai de la recommandation</label>
                <div className="flex items-center">
                  <Clock className="h-3 w-3 text-gray-400 mr-1" />
                  <p className="text-sm text-gray-900">
                    {new Date(action.delay_of_recommendation).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {action?.started_at && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Démarré le</label>
                  <p className="text-sm text-gray-900">
                    {new Date(action.started_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              )}
              {action?.completed_at && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Complété le</label>
                  <p className="text-sm text-gray-900">
                    {new Date(action.completed_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              )}
              {action?.achieved_date && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Atteint le</label>
                  <p className="text-sm text-gray-900">
                    {new Date(action.achieved_date).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Organization */}
        {organization && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <Building2 className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Organisation</h2>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm text-gray-900">{organization.name}</p>
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 text-xs rounded ${
                  organization.is_subscribed 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {organization.is_subscribed ? 'Abonné' : 'Non abonné'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Recommendation Owner */}
        {recommendation_owner && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <User className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Propriétaire de la recommandation</h2>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm text-gray-900">{recommendation_owner.full_name || 'Inconnu'}</p>
              {recommendation_owner.email && (
                <div className="flex items-center">
                  <Mail className="h-3 w-3 text-gray-400 mr-1" />
                  <p className="text-xs text-gray-600">{recommendation_owner.email}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Created By & Last Updated By */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {created_by && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <User className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Créé par</h2>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-gray-900">{created_by.full_name || 'Inconnu'}</p>
                {created_by.email && (
                  <div className="flex items-center">
                    <Mail className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-xs text-gray-600">{created_by.email}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {last_updated_by && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <User className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Dernière modification par</h2>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-gray-900">{last_updated_by.full_name || 'Inconnu'}</p>
                {last_updated_by.email && (
                  <div className="flex items-center">
                    <Mail className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-xs text-gray-600">{last_updated_by.email}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Follow-ups */}
        {followups && followups.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <TrendingUp className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Suivis</h2>
              </div>
              <div className="text-xs text-gray-500">
                {statistics?.total_followups || followups.length} suivi(s)
              </div>
            </div>

            <div className="space-y-3">
              {followups.map((followup) => (
                <div key={followup.id} className="p-4 rounded-lg border border-gray-200">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm font-medium text-gray-900">Progression: {followup.progression}%</span>
                        <span className={`px-2 py-1 text-xs rounded ${
                          followup.status === 'completed' ? 'bg-green-100 text-green-700' :
                          followup.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {followup.status}
                        </span>
                      </div>
                      {followup.notes && (
                        <p className="text-sm text-gray-700 mb-2">{followup.notes}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Attachments */}
                  {followup.attachments && followup.attachments.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center mb-2">
                        <Paperclip className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-xs font-medium text-gray-600">
                          {followup.attachments.length} pièce(s) jointe(s)
                        </span>
                      </div>
                      <div className="space-y-2">
                        {followup.attachments.map((att) => (
                          <div key={att.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-900 truncate">{att.file_name}</p>
                              <p className="text-xs text-gray-500">
                                {(att.file_size / 1024).toFixed(2)} KB
                              </p>
                            </div>
                            <button
                              onClick={() => handleDownloadAttachment(att.id, att.file_name)}
                              disabled={downloadingAttachment === att.id}
                              className="ml-2 p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                              title="Télécharger"
                            >
                              {downloadingAttachment === att.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-3 text-xs text-gray-500 mt-2">
                    <span>Créé le {new Date(followup.created_at).toLocaleDateString('fr-FR')}</span>
                    {followup.created_by && (
                      <>
                        <span>•</span>
                        <span>Par {followup.created_by.name || followup.created_by.email}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        
       
      </div>
    </div>
  );
}
